'use client'

import { useState, useEffect } from 'react'
import clsx from 'clsx'
import styles from './index.module.css'

interface Snowflake {
  id: number
  left: string
  delay: string
  duration: string
  opacity: number
  size: string
}

interface SnowfallEffectProps {
  isActive: boolean
  durationMs?: number
  intensity?: number
  gradualStart?: boolean
}

const generateSnowflake = (id: number, initial = false, gradualStart = false): Snowflake => {
  const duration = 2 + Math.random() * 3
  let delay = '0s'

  if (initial) {
    delay = gradualStart ? `${Math.random() * 2}s` : `${-Math.random() * duration}s`
  }

  return {
    id,
    left: `${Math.random() * 100}%`,
    delay,
    duration: `${duration}s`,
    opacity: Math.random() * 0.6 + 0.4,
    size: `${10 + Math.random() * 10}px`,
  }
}

export function SnowfallEffect({ isActive, durationMs, intensity = 1, gradualStart = false }: SnowfallEffectProps) {
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [snowflakes, setSnowflakes] = useState<Snowflake[]>([])

  useEffect(() => {
    if (!isActive) {
      const resetTimer = setTimeout(() => {
        setIsFadingOut(false)
        setSnowflakes([])
      }, 0)
      return () => clearTimeout(resetTimer)
    }

    // Generate initial snowflakes
    const initialFlakes: Snowflake[] = []
    const count = Math.floor(50 * intensity)
    for (let i = 0; i < count; i++) {
      initialFlakes.push(generateSnowflake(i, true, gradualStart))
    }
    // eslint-disable-next-line
    setSnowflakes(initialFlakes)

    // If no duration is specified, don't fade out
    if (durationMs === undefined) {
      return
    }

    // Start fading out 1s before duration ends
    const fadeOutTime = Math.max(0, durationMs - 1000)
    const timer = setTimeout(() => {
      setIsFadingOut(true)
    }, fadeOutTime)

    return () => clearTimeout(timer)
  }, [isActive, durationMs, intensity, gradualStart])

  // Handle continuous snowfall
  // Removed manual timeout handling in favor of onAnimationEnd

  if (!isActive) return null

  return (
    <div
      className={clsx(styles.snowfallContainer, {
        [styles.fadeOut]: isFadingOut,
      })}
    >
      {snowflakes.map((flake) => (
        <div
          key={flake.id}
          className={styles.snowflake}
          style={{
            left: flake.left,
            animationDelay: flake.delay,
            animationDuration: flake.duration,
            opacity: flake.opacity,
            fontSize: flake.size,
          }}
          onAnimationEnd={() => {
            if (!isFadingOut && isActive) {
              setSnowflakes((prev) => prev.map((f) => (f.id === flake.id ? generateSnowflake(Date.now() + Math.random()) : f)))
            }
          }}
        >
          ❄
        </div>
      ))}
    </div>
  )
}
